import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../Redux/Store/Hooks";
import { listRole } from "../../Redux/Slices/Role/ListRoleSlice";
import { listYearlevel_map } from "../../Redux/Slices/Yearlevel_map/ListYearlevel_mapSlice";
import {
    getListRoleScreenYGPermission,
    clearListRoleScreenYGPermission
} from "../../Redux/Slices/RoleScreenYGPermission/ListRoleScreenYGPermissionSlice";
import {
    manageRoleScreenYGPermission,
    clearManageRoleScreenYGPermission
} from "../../Redux/Slices/RoleScreenYGPermission/ManageRoleScreenYGPermissionSlice";
import ButtonField from "../../Components/Button/ButtonField";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import Table from "../../Components/Table/Table";
import { Columns } from "../../Components/Table/ITable";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import CustomFooter from "../../Components/Table/components/CustomFooter";
import Select from "../../Components/withoutControllerComponents/Select/Select";
import Checkbox from "../../Components/Checkbox/Checkbox";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import { Box } from "@mui/material";

const RoleScreenYGPermissionPage = () => {
    const dispatch = useDispatch();

    const [selectedRole, setSelectedRole] = useState<number | null>(null);
    const [selectedYearGroup, setSelectedYearGroup] = useState<number | null>(null);

    // Local state to manage permission changes before submitting
    const [permissionList, setPermissionList] = useState<any[]>([]);

    // Selectors
    const roleListState = useAppSelector((state) => state.ListRoleSlice);
    const yearGroupListState = useAppSelector((state) => state.ListYearlevel_mapSlice);
    const listPermissionState = useAppSelector((state) => state.ListRoleScreenYGPermissionSlice);
    const managePermissionState = useAppSelector((state) => state.ManageRoleScreenYGPermissionSlice);

    const rolesData = roleListState?.data?.data || [];
    const yearGroupsData = yearGroupListState?.data?.data || [];
    const fetchedPermissions = listPermissionState?.data?.data || [];
    const isSubmitting = managePermissionState?.isLoading;
    const manageResponse = managePermissionState?.data;

    // Check if student role
    const isStudentRole = useMemo(() => {
        const role = rolesData.find((r: any) => r.id === selectedRole);
        return role?.name?.toLowerCase().includes("student");
    }, [rolesData, selectedRole]);

    // Format options for Select 
    const RoleOptions = useMemo(() => rolesData.filter((r: any) => r.name?.toLowerCase() !== "admin").map((r: any) => ({ label: r.name, value: r.id })), [rolesData]);
    const YearOptions = useMemo(() => yearGroupsData.map((yg: any) => ({ label: yg.yeargroup?.name || "Unknown", value: yg.id })), [yearGroupsData]);

    useEffect(() => {
        dispatch(listRole());
        dispatch(listYearlevel_map());
    }, [dispatch]);

    useEffect(() => {
        if (selectedRole) {
            dispatch(getListRoleScreenYGPermission({
                role_id: Number(selectedRole),
                yeargroup_country_map_id: selectedYearGroup ? Number(selectedYearGroup) : undefined
            }));
        }
    }, [dispatch, selectedRole, selectedYearGroup]);

    useEffect(() => {
        if (selectedRole && fetchedPermissions) {
            const mapped = fetchedPermissions.map((p: any) => {
                const perms = p.permissions || p;
                return {
                    id: p.screen_id, // Table likely needs distinct 'id'
                    screen_id: p.screen_id,
                    screen_name: p.screen_name,
                    view: perms.view ?? perms.view_permission ?? false,
                    add: perms.add ?? perms.add_permission ?? false,
                    edit: perms.edit ?? perms.edit_permission ?? false,
                    delete: perms.delete ?? perms.delete_permission ?? false,
                    is_visible: perms.is_visible ?? true,
                };
            });
            setPermissionList(mapped);
        } else if (!selectedRole) {
            setPermissionList([]);
        }
    }, [fetchedPermissions, selectedRole]);

    useEffect(() => {
        return () => {
            dispatch(clearManageRoleScreenYGPermission());
        }
    }, [dispatch]);

    const handleCheckboxChange = (id: string, field: string, checked: boolean) => {
        setPermissionList(prevList => prevList.map(item =>
            item.screen_id === id ? { ...item, [field]: checked } : item
        ));
    };

    const columns: Columns = [
        {
            field: "screen_name",
            renderHeader: () => <b>Menus</b>,
            flex: 3,
            type: "string",
            align: "left",
            headerAlign: "center",
            maxWidth: 250,
        },
        {
            field: "view",
            renderHeader: () => <b>View</b>,
            flex: 1,
            type: "boolean",
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Checkbox
                    color="success"
                    checked={params.value}
                    onChange={(e) => handleCheckboxChange(params.row.screen_id, 'view', e.target.checked)}
                />
            )
        },
        {
            field: "add",
            renderHeader: () => <b>Add</b>,
            flex: 1,
            type: "boolean",
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Checkbox
                    color="success"
                    checked={params.value}
                    onChange={(e) => handleCheckboxChange(params.row.screen_id, 'add', e.target.checked)}
                />
            )
        },
        {
            field: "edit",
            renderHeader: () => <b>Edit</b>,
            flex: 1,
            type: "boolean",
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Checkbox
                    color="success"
                    checked={params.value}
                    onChange={(e) => handleCheckboxChange(params.row.screen_id, 'edit', e.target.checked)}
                />
            )
        },
        {
            field: "delete",
            renderHeader: () => <b>Delete</b>,
            flex: 1,
            type: "boolean",
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Checkbox
                    color="success"
                    checked={params.value}
                    onChange={(e) => handleCheckboxChange(params.row.screen_id, 'delete', e.target.checked)}
                />
            )
        }
    ];

    const handleSubmit = () => {
        if (!selectedRole) return;

        const permissionsPayload = permissionList.map(p => ({
            screen_id: p.screen_id,
            view: p.view,
            add: p.add,
            edit: p.edit,
            delete: p.delete,
            is_visible: p.is_visible
        }));

        dispatch(manageRoleScreenYGPermission({
            role_id: Number(selectedRole),
            yeargroup_country_map_id: selectedYearGroup ? Number(selectedYearGroup) : undefined,
            permissions: permissionsPayload
        }));
    };

    const setParentDialog = (open: any) => {
        const isOpen = typeof open === 'function' ? open(true) : open;
        if (!isOpen) {
            dispatch(clearManageRoleScreenYGPermission());
            dispatch(getListRoleScreenYGPermission({
                role_id: Number(selectedRole),
                yeargroup_country_map_id: selectedYearGroup ? Number(selectedYearGroup) : undefined
            }));
        }
    }

    return (
        <Box>
            <Table
                rows={permissionList}
                columns={columns}
                getRowId={(row) => row.id} // or row.screen_id
            >
                <CustomToolbar className="top_toolbar">
                    <Select
                        className="select_option"
                        label="Select Role"
                        size="small"
                        options={RoleOptions}
                        value={RoleOptions.find(r => r.value === selectedRole) || null}
                        onChange={(_, v) => {
                            const roleId = (v as AutocompleteOption)?.value as number || null;
                            setSelectedRole(roleId);
                            setSelectedYearGroup(null);
                            setPermissionList([]);
                            if (!roleId) {
                                dispatch(clearListRoleScreenYGPermission());
                            }
                        }}
                        sx={{ minWidth: "300px" }}
                    />

                    {isStudentRole && (
                        <Select
                            className="select_option"
                            label="Select Year Group"
                            size="small"
                            options={YearOptions}
                            value={YearOptions.find(y => y.value === selectedYearGroup) || null}
                            onChange={(_, v) => {
                                setSelectedYearGroup((v as AutocompleteOption)?.value as number || null);
                            }}
                            sx={{ minWidth: "300px" }}
                        />
                    )}
                </CustomToolbar>

                <CustomFooter className="bottom_toolbar">
                    <ButtonField
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !selectedRole}
                        sx={{
                            "&:hover": {
                                backgroundColor: "#009DFF",
                            },
                        }}
                    >
                        {isSubmitting ? "Saving..." : "Submit"}
                    </ButtonField>
                </CustomFooter>
            </Table>

            <ResponseDialog response={manageResponse} parentDialog={setParentDialog} />
        </Box>
    );
};

export default RoleScreenYGPermissionPage;
